import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { ProductoColor } from '../../types';

interface ColorIndicatorsProps {
  colores?: ProductoColor[];
  loading?: boolean;
}

// Mapeo de nombres de colores a códigos hex
const COLOR_MAP: Record<string, string> = {
  'rojo': '#E53935',
  'azul': '#1976D2',
  'verde': '#43A047',
  'amarillo': '#FBC02D',
  'negro': '#212121',
  'blanco': '#FFFFFF',
  'gris': '#757575',
  'rosa': '#EC407A',
  'naranja': '#FB8C00',
  'morado': '#7E57C2',
  'marrón': '#795548',
  'beige': '#D7CCC8',
  'azul_claro': '#42A5F5',
  'azul_marino': '#1565C0',
  'verde_claro': '#66BB6A',
  'otro': '#9E9E9E',
};

/**
 * Resuelve el nombre de un color a su código hex
 * Si no está en el mapa, devuelve gris por defecto
 */
function getColorHex(colorName: string): string {
  const normalized = colorName.toLowerCase().trim();
  return COLOR_MAP[normalized] || '#9E9E9E';
}

/**
 * Componente que muestra círculos de color interactivos
 * con tooltips que revelan el stock de cada color
 */
export const ColorIndicators = React.memo(function ColorIndicators({
  colores,
  loading = false,
}: ColorIndicatorsProps) {
  // Si no hay colores, mostrar "-"
  if (!colores || colores.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight: '22px',
        }}
      >
        <span style={{ fontSize: '0.875rem', color: '#999' }}>-</span>
      </Box>
    );
  }

  // Si está cargando, mostrar skeleton
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {[1, 2, 3, 4, 5].map((idx) => (
          <Box
            key={`skeleton-${idx}`}
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: '#E0E0E0',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
    );
  }

  // Obtener los primeros 5 colores y calcular si hay más
  const visibleColors = colores.slice(0, 5);
  const remainingCount = Math.max(0, colores.length - 5);

  // Calcular aria-label para accesibilidad
  const colorLabels = colores
    .map((c) => `${c.color} (${c.stock})`)
    .join(', ');
  const ariaLabel = `Colores disponibles: ${colorLabels}`;

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      sx={{
        display: 'flex',
        gap: { xs: 0.25, sm: 0.5 },
        alignItems: 'center',
        justifyContent: 'flex-start',
        mt: 0.75,
        mb: 0.75,
        flexWrap: 'wrap',
      }}
    >
      {/* Renderizar los círculos de color */}
      {visibleColors.map((color, idx) => {
        const colorHex = getColorHex(color.color);
        const isZeroStock = color.stock === 0;

        return (
          <Tooltip
            key={`color-${idx}-${color.color}`}
            title={`${color.color} - Stock: ${color.stock}`}
            placement="top"
            arrow
          >
            <Box
              sx={{
                width: { xs: 12, sm: 14 },
                height: { xs: 12, sm: 14 },
                minWidth: { xs: 12, sm: 14 },
                minHeight: { xs: 12, sm: 14 },
                borderRadius: '50%',
                backgroundColor: colorHex,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                opacity: isZeroStock ? 0.5 : 1,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  width: { xs: 14, sm: 18 },
                  height: { xs: 14, sm: 18 },
                  margin: { xs: -1, sm: -2 },
                  transform: 'scale(1.3)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                  zIndex: 10,
                },
              }}
            />
          </Tooltip>
        );
      })}

      {/* Si hay más de 5 colores, mostrar "+N" */}
      {remainingCount > 0 && (
        <Tooltip
          title={`+${remainingCount} colores más`}
          placement="top"
          arrow
        >
          <Box
            sx={{
              width: { xs: 12, sm: 14 },
              height: { xs: 12, sm: 14 },
              minWidth: { xs: 12, sm: 14 },
              minHeight: { xs: 12, sm: 14 },
              borderRadius: '50%',
              backgroundColor: '#E0E0E0',
              border: '1px solid rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.6rem',
              fontWeight: 600,
              color: '#424242',
              cursor: 'default',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                width: { xs: 14, sm: 18 },
                height: { xs: 14, sm: 18 },
                margin: { xs: -1, sm: -2 },
                transform: 'scale(1.3)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                zIndex: 10,
              },
            }}
          >
            +{remainingCount}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
});

// Configurar display name para debugging
ColorIndicators.displayName = 'ColorIndicators';
